import SettingsIcon from '@mui/icons-material/Settings';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Slider,
    Switch,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import AppConfig, { saveAppConfig } from '../../utils/AppConfig';
import { useTrackUsage } from '../../hooks/useTrackUsage';

const AppConfigDialog = ({ open, onClose }) => {
    useTrackUsage('AppConfigDialog', 'dialog', 'settings', open);
    const [historySize, setHistorySize] = useState(AppConfig.historySize);
    const [enableMirroredGujaratiSave, setEnableMirroredGujaratiSave] = useState(AppConfig.enableMirroredGujaratiSave);
    const [enableWaveforms, setEnableWaveforms] = useState(AppConfig.enableWaveforms);
    const [clearDubbingAudioFilesIn24Hours, setClearDubbingAudioFilesIn24Hours] = useState(AppConfig.clearDubbingAudioFilesIn24Hours);
    const [enableSeekDebug, setEnableSeekDebug] = useState(AppConfig.enableSeekDebug);
    const [enableSeekDebugNetworkCapture, setEnableSeekDebugNetworkCapture] = useState(
        AppConfig.enableSeekDebugNetworkCapture,
    );
    const [sessionInactivityTimeoutMinutes, setSessionInactivityTimeoutMinutes] = useState(
        AppConfig.sessionInactivityTimeoutMinutes,
    );

    const waveformChanged = enableWaveforms !== AppConfig.enableWaveforms;

    const handleSave = () => {
        saveAppConfig({
            historySize,
            enableMirroredGujaratiSave,
            enableWaveforms,
            clearDubbingAudioFilesIn24Hours,
            enableSeekDebug,
            enableSeekDebugNetworkCapture,
            sessionInactivityTimeoutMinutes,
        });
        onClose();
        if (waveformChanged) {
            // Waveform is initialised once when the editor loads — a reload is needed
            window.location.reload();
        }
    };

    const handleCancel = () => {
        // Reset local state to current AppConfig values
        setHistorySize(AppConfig.historySize);
        setEnableMirroredGujaratiSave(AppConfig.enableMirroredGujaratiSave);
        setEnableWaveforms(AppConfig.enableWaveforms);
        setClearDubbingAudioFilesIn24Hours(AppConfig.clearDubbingAudioFilesIn24Hours);
        setEnableSeekDebug(AppConfig.enableSeekDebug);
        setEnableSeekDebugNetworkCapture(AppConfig.enableSeekDebugNetworkCapture);
        setSessionInactivityTimeoutMinutes(AppConfig.sessionInactivityTimeoutMinutes);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon fontSize="small" />
                App Settings
            </DialogTitle>

            <DialogContent dividers>
                {/* ── Editor ── */}
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}
                >
                    EDITOR
                </Typography>

                <Box sx={{ px: 1, mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                        <Typography variant="body2">Undo history size</Typography>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 600, minWidth: 28, textAlign: 'right' }}>
                            {historySize}
                        </Typography>
                    </Box>
                    <Slider
                        value={historySize}
                        onChange={(_, v) => setHistorySize(v)}
                        min={5}
                        max={100}
                        step={5}
                        marks={[
                            { value: 5, label: '5' },
                            { value: 25, label: '25' },
                            { value: 50, label: '50' },
                            { value: 100, label: '100' },
                        ]}
                        valueLabelDisplay="auto"
                        size="small"
                    />
                    <Typography variant="caption" color="text.secondary">
                        Smaller values reduce memory usage; larger values allow deeper undo chains.
                    </Typography>
                </Box>

                
                <Divider sx={{ my: 2 }} />

                {/* ── Session ── */}
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}
                >
                    SESSION
                </Typography>

                <Box sx={{ px: 1, mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                        <Typography variant="body2">Inactivity timeout (minutes)</Typography>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 600, minWidth: 28, textAlign: 'right' }}>
                            {sessionInactivityTimeoutMinutes}
                        </Typography>
                    </Box>
                    <Slider
                        value={sessionInactivityTimeoutMinutes}
                        onChange={(_, v) => setSessionInactivityTimeoutMinutes(v)}
                        min={5}
                        max={60}
                        step={5}
                        marks={[
                            { value: 5, label: '5' },
                            { value: 15, label: '15' },
                            { value: 30, label: '30' },
                            { value: 60, label: '60' },
                        ]}
                        valueLabelDisplay="auto"
                        size="small"
                    />
                    <Typography variant="caption" color="text.secondary">
                        Ends an edit session and returns to home after this many minutes of inactivity.
                    </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* ── Performance ── */}
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}
                >
                    PERFORMANCE
                </Typography>

                <FormControlLabel
                    control={
                        <Switch
                            checked={enableWaveforms}
                            onChange={(e) => setEnableWaveforms(e.target.checked)}
                            size="small"
                        />
                    }
                    label={
                        <Box>
                            <Typography variant="body2">Waveform visualiser</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Load actual video audio into the timeline waveform. Increases CPU and memory usage.
                            </Typography>
                        </Box>
                    }
                    sx={{ alignItems: 'flex-start' }}
                />

                {waveformChanged && (
                    <Alert severity="info" sx={{ mt: 2 }} variant="outlined">
                        Waveform change takes effect after the page reloads.
                    </Alert>
                )}

                <Divider sx={{ my: 2 }} />

                {/* ── Dubbing ── */}
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}
                >
                    DUBBING
                </Typography>

                <FormControlLabel
                    control={
                        <Switch
                            checked={clearDubbingAudioFilesIn24Hours}
                            onChange={(e) => setClearDubbingAudioFilesIn24Hours(e.target.checked)}
                            size="small"
                        />
                    }
                    label={
                        <Box>
                            <Typography variant="body2">Auto-clear dubbing audio in 24 hours</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Automatically remove generated dubbing audio files after 24 hours.
                            </Typography>
                        </Box>
                    }
                    sx={{ alignItems: 'flex-start', mb: 1 }}
                />

                <Divider sx={{ my: 2 }} />

                {/* ── Languages ── */}
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}
                >
                    LANGUAGES
                </Typography>

                <FormControlLabel
                    control={
                        <Switch
                            checked={enableMirroredGujaratiSave}
                            onChange={(e) => setEnableMirroredGujaratiSave(e.target.checked)}
                            size="small"
                        />
                    }
                    label={
                        <Box>
                            <Typography variant="body2">Gujarati mirror save</Typography>
                            <Typography variant="caption" color="text.secondary">
                                On every English save, write the mirrored Gujarati VTT with updated timings. For backend testing purposes only.
                            </Typography>
                        </Box>
                    }
                    sx={{ alignItems: 'flex-start', mb: 1 }}
                />

                <Divider sx={{ my: 2 }} />

                {/* ── Diagnostics ── */}
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}
                >
                    DIAGNOSTICS
                </Typography>

                <FormControlLabel
                    control={
                        <Switch checked={enableSeekDebug} onChange={(e) => setEnableSeekDebug(e.target.checked)} size="small" />
                    }
                    label={
                        <Box>
                            <Typography variant="body2">Subtitle seek debug mode</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Captures detailed seek lifecycle events, user context, and playback diagnostics for bug reports.
                            </Typography>
                        </Box>
                    }
                    sx={{ alignItems: 'flex-start', mb: 1 }}
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={enableSeekDebugNetworkCapture}
                            onChange={(e) => setEnableSeekDebugNetworkCapture(e.target.checked)}
                            size="small"
                            disabled={!enableSeekDebug}
                        />
                    }
                    label={
                        <Box>
                            <Typography variant="body2">Include network quality and request diagnostics</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Adds effective connection type, RTT/downlink, and request timing tags to debug logs.
                            </Typography>
                        </Box>
                    }
                    sx={{ alignItems: 'flex-start', mb: 1 }}
                />

            </DialogContent>

            <DialogActions>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button onClick={handleSave} variant="contained">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AppConfigDialog;
